import React, { useEffect, useState } from 'react';
import DashboardCard from './DashboardCard';
import DoughnutChart from './DoughnutChart';
import { Caption, H3, PageSection } from 'common/styles/page';
import styled from 'styled-components';

import { useApiPortalV1AssessmentsResultsListQuery } from 'areas/assessment-schedule/api';
import { useNavigate } from 'react-router';

import { AggregateType } from 'common/constants/AggregateType';
const VERY_POSITIVE = 0;
const SOMEWHAT_POSITIVE = 1;
const NEUTRAL = 2;
const SOMEWHAT_NEGATIVE = 3;
const VERY_NEGATIVE = 4;

interface DashboardSentimentProps {
  assessment_period_id: number;
  team_id?: number;
  inline?: boolean;
}

const DashboardSentiment = ({ assessment_period_id, team_id, inline }: DashboardSentimentProps) => {
  const navigate = useNavigate();

  interface SentimentScore {
    id: number;
    label: string;
    caption: string;
    value: number;
    color: string;
  }

  const [sentimentScores, setSentimentScores] = useState<SentimentScore[]>([]);
  const { data, isLoading, error } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.SENTIMENT_DISTRIBUTION,
    assessmentPeriodId: assessment_period_id,
  });

  function safeNumber(val: number | undefined) {
    return val ?? 0;
  }

  useEffect(() => {
    if (data && data.length > 0) {
      const result = data[0]?.result || {};

      // Prefer team distribution when team_id is present
      let distribution = result?.org_distribution?.distribution;

      if (typeof team_id !== 'undefined' && Array.isArray(result?.team_distributions)) {
        const found = result.team_distributions.find((td: any) => Number(td.team_id) === Number(team_id));
        if (found && Array.isArray(found.distribution)) {
          distribution = found.distribution;
        }
      }

      let scores: SentimentScore[] = [];

      if (Array.isArray(distribution) && distribution.length > 0) {
        // Build a lookup of bucket -> percentage
        const map = distribution.reduce<Record<string, number>>((acc, cur) => {
          acc[cur.bucket] = Number(cur.percentage) || 0;
          return acc;
        }, {});

        scores = [
          {
            id: VERY_NEGATIVE,
            label: 'Very Negative',
            caption: 'Dissatisfied and disengaged',
            value: map['Very Negative'] ?? 0,
            color: 'rgba(255, 50, 90, .8)'
          },
          {
            id: SOMEWHAT_NEGATIVE,
            label: 'Somewhat Negative',
            caption: 'Concerned, wants improvement',
            value: map['Negative'] ?? 0,
            color: 'rgba(255, 50, 90, .4)'
          },
          {
            id: NEUTRAL,
            label: 'Neutral',
            caption: 'Mixed or indifferent feelings',
            value: map['Neutral'] ?? 0,
            color: 'rgba(255, 216, 111, 1)'
          },
          {
            id: SOMEWHAT_POSITIVE,
            label: 'Somewhat Positive',
            caption: 'Satisfied, room to grow',
            value: map['Positive'] ?? 0,
            color: 'rgba(129, 199, 132, .5)'
          },
          {
            id: VERY_POSITIVE,
            label: 'Very Positive',
            caption: 'Thriving and highly engaged',
            value: map['Very Positive'] ?? 0,
            color: 'rgba(129, 199, 132, 1)'
          }
        ];
      }

      setSentimentScores(scores);
    }
  }, [data, team_id]);

  // Display loading state
  if (isLoading) {
    return (
      <DashboardCard title="Sentiment Feedback">
        <p>Loading sentiment data...</p>
      </DashboardCard>
    );
  }

  // Display error state
  if (error || !data) {
    return (
      <DashboardCard title="Sentiment Feedback">
        <p>Unable to load sentiment data. Please try again later.</p>
      </DashboardCard>
    );
  }

  // Display fallback if no data is available
  if (data.length === 0 || sentimentScores.length === 0) {
    const empty = (
      <p>No sentiment data available for this assessment period.</p>
    );
    return inline ? empty : <DashboardCard title="Sentiment Feedback">{empty}</DashboardCard>;
  }

  const innerContent = (
    <PageSection display="flex" flexDirection="row" justifyContent="space-between" flex="2">
      <PageSection flex="1">
        <DoughnutChart chartData={sentimentScores} size="large" />
      </PageSection>
      <PageSection
        flex="1"
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        justifyContent="space-between"
        gap="8px"
      >
        {sentimentScores.map((item, index) => (
          <PageSection
            gap="2px"
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            key={index}
          >
            <PageSection gap="8px" display="flex" flexDirection="row" alignItems="flex-start">
              <LegendBox color={item.color} />
              <H3>{item.label}</H3>
            </PageSection>
            <Caption style={{ paddingLeft: 30 }}>{item.caption}</Caption>
          </PageSection>
        ))}
      </PageSection>
    </PageSection>
  );

  return inline ? (
    innerContent
  ) : (
    <DashboardCard title="Sentiment Feedback" onClick={() => navigate('/app/analytics/?tab=2')}>
      {innerContent}
    </DashboardCard>
  );
};

export default DashboardSentiment;

const LegendBox = styled.div<{ color: string }>`
  width: 20px;
  height: 20px;
  background-color: ${props => props.color};
  border-radius: 3px;
`;



